import React, { useState, useCallback } from 'react';
import { useToggle } from 'react-use-v13';
import { ScheduledSessionsTable } from './ScheduledSessionsTable';
import { CreateSessionModal } from './CreateSessionModal';
import { CreateSessionSuccessModal } from './CreateSessionSuccessModal';
import { twMerge } from 'tailwind-merge';
import { useUser } from 'react-scale/components/ui/UserContext';
import { ViewHeader } from 'react-scale/corp/views/CorpProjectGroupManager/components/layout';
import { useProjectId } from 'react-scale/corp/views/CorpProjectGroupManager/hooks/useProjectId';
import { TrainingMenu } from 'react-scale/corp/views/CorpProjectGroupManager/constants';
import { IUserJson } from '@scale/shared/types/user';
import { Text, Button, Flex, Switch, Icon } from '@scale/scaleui-radix';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { useCreateWebinarSession } from '../hooks/useCreateWebinarSessionStore';

interface ScheduledSessionsPageProps {
  className?: string;
  showHeader?: boolean;
  user?: IUserJson;
  projectId?: string;
}

const useProject = (enabled: boolean) => {
  if (!enabled) return undefined;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useProjectId();
};

const ScheduledSessionsPage: React.FC<ScheduledSessionsPageProps> = ({
  className,
  showHeader,
  user: userFromProps,
  projectId,
}) => {
  const userFromContext = useUser();
  const user = userFromProps || userFromContext;
  const genaiOpsHub = location.pathname.includes('genai-ops-hub');
  const projectIdFromUrl = useProject(genaiOpsHub);
  const project = projectId || projectIdFromUrl;
  const [showCreateSessionModal, toggleCreateSessionModal] = useToggle(false);
  const [showCreateSessionSuccessModal, toggleCreateSessionSuccessModal] = useToggle(false);
  const [showElapsedSessions, setShowElapsedSessions] = useState<boolean>(true);

  const { newlyCreatedSessions } = useCreateWebinarSession();

  const onCreateSessionSuccess = useCallback(() => {
    toggleCreateSessionModal();
    toggleCreateSessionSuccessModal();
  }, [toggleCreateSessionModal, toggleCreateSessionSuccessModal]);

  return (
    <Flex direction="column" width="100%" height="100%">
      {genaiOpsHub && <ViewHeader title={TrainingMenu.WebinarSessions} />}
      <Flex
        direction="column"
        overflowY="auto"
        gap="4"
        p="4"
        className={twMerge('scaleui scale-tailwind', className)}
      >
        {!!showHeader && <Text className="whitespace-nowrap">Webinar Sessions</Text>}
        <Flex align="center">
          <Text size="3" weight="medium">
            Your scheduled sessions
          </Text>
          <Flex className="ml-auto" gap="2" align="center">
            <Flex gap="2" align="center">
              <Switch
                size="2"
                checked={showElapsedSessions}
                onCheckedChange={setShowElapsedSessions}
              />
              Show past sessions
            </Flex>
            <Button
              ml="4"
              variant="outline"
              color="neutral"
              size="2"
              onClick={() => window.open('https://scale.atlassian.net/wiki/x/VgDTNw', '_blank')}
            >
              <Icon icon={faArrowUpRightFromSquare} size="2" label="link" />
              Documentation
            </Button>
            <Button variant="solid" color="accent" size="2" onClick={toggleCreateSessionModal}>
              + New session
            </Button>
          </Flex>
        </Flex>
        <ScheduledSessionsTable
          user={user}
          showElapsedSessions={showElapsedSessions}
          projectId={project}
        />
        <CreateSessionModal
          user={user}
          showModal={showCreateSessionModal}
          toggleModal={toggleCreateSessionModal}
          onSuccess={onCreateSessionSuccess}
          projectId={project}
        />
        <CreateSessionSuccessModal
          newlyCreatedSessions={newlyCreatedSessions}
          showModal={showCreateSessionSuccessModal}
          toggleModal={toggleCreateSessionSuccessModal}
        />
      </Flex>
    </Flex>
  );
};

export default ScheduledSessionsPage;
